import {
  type CheckboxGroupProps as ArkCheckboxGroupProps,
  Checkbox as BaseCheckbox,
  CheckboxGroup as BaseCheckboxGroup,
  type CheckboxRootProps,
  Field,
  type FieldRootProps,
} from "@ark-ui/react";
import { Check, CheckFat, type IconProps, Minus } from "@phosphor-icons/react";
import clsx from "clsx";
import * as React from "react";
import getFormHelperText, {
  type HelperTextProps,
} from "../../helper/HelperTextIcon.js";
import {
  FormLabel,
  type FormLabelClassNames,
  type LabelOptionProps,
} from "../../shared/form-label/FormLabel.js";

interface CheckboxProps extends CheckboxRootProps {
  className?: string;
  invertColors?: boolean;
  iconProps?: IconProps;
  label?: string;
  supportingText?: string;
  fieldProps?: FieldRootProps;
  classNames?: {
    root?: string;
    control?: string;
    indicator?: string;
    label?: string;
  };
  size?: "sm" | "md" | "lg";
  fontWeight?: "regular" | "medium" | "semibold" | "bold";
}

const CheckboxGroupContext = React.createContext<{
  size?: "sm" | "md" | "lg";
  fontWeight?: "regular" | "medium" | "semibold" | "bold";
}>({
  size: "md",
  fontWeight: "regular",
});

const useCheckboxGroupContext = () => {
  return React.useContext(CheckboxGroupContext);
};

const Checkbox = React.forwardRef<
  React.ElementRef<typeof BaseCheckbox.HiddenInput>,
  CheckboxProps
>(
  (
    {
      className,
      classNames,
      size,
      invertColors,
      fontWeight,
      label,
      fieldProps,
      iconProps,
      supportingText,
      ...rest
    },
    ref,
  ) => {
    const context = useCheckboxGroupContext();
    const checkboxSize = size || context?.size || "md";
    const checkboxWeight = fontWeight || context?.fontWeight || "regular";

    return (
      <Field.Root {...fieldProps}>
        <BaseCheckbox.Root
          className={clsx(
            "ox-reset ox-CheckboxRoot",
            classNames?.root,
            className,
          )}
          data-size={checkboxSize}
          {...rest}
        >
          <BaseCheckbox.Control
            data-size={size}
            className={clsx(
              "ox-reset ox-CheckboxControl",
              invertColors && "inverted",
              classNames?.control,
            )}
          >
            <BaseCheckbox.Indicator
              className={clsx(
                "ox-reset ox-CheckboxIndicator",
                invertColors && "inverted",
                classNames?.indicator,
              )}
            >
              {invertColors ? (
                <CheckFat
                  weight="fill"
                  fill="currentColor"
                  className={clsx(
                    "ox-reset ox-CheckboxIndicatorIcon",
                    invertColors && "inverted",
                    iconProps?.className,
                  )}
                  {...iconProps}
                />
              ) : (
                <Check
                  weight="bold"
                  fill="currentColor"
                  className={clsx(
                    "ox-reset ox-CheckboxIndicatorIcon",
                    invertColors && "inverted",
                    iconProps?.className,
                  )}
                  {...iconProps}
                />
              )}
            </BaseCheckbox.Indicator>
            <BaseCheckbox.Indicator
              indeterminate
              className={clsx(
                "ox-reset ox-CheckboxIndicator",
                invertColors && "inverted",
                classNames?.indicator,
              )}
            >
              <Minus
                weight="bold"
                fill="currentColor"
                className={clsx("ox-reset ox-CheckboxIndicatorIcon")}
              />
            </BaseCheckbox.Indicator>
          </BaseCheckbox.Control>
          {label ? (
            <BaseCheckbox.Label
              data-size={checkboxSize}
              className={clsx(
                "ox-reset ox-CheckboxLabel",
                invertColors && "inverted",
                checkboxSize === "sm"
                  ? "ox-fz-2xs"
                  : checkboxSize === "md"
                    ? "ox-fz-sm"
                    : checkboxSize === "lg"
                      ? "ox-fz-md"
                      : "ox-fz-sm",
                checkboxWeight === "regular"
                  ? "ox-fw-regular"
                  : checkboxWeight === "medium"
                    ? "ox-fw-medium"
                    : checkboxWeight === "bold"
                      ? "ox-fw-bold"
                      : checkboxWeight === "semibold"
                        ? "ox-fw-semibold"
                        : "ox-fw-regular",
                classNames?.label,
              )}
            >
              {label}
            </BaseCheckbox.Label>
          ) : null}
          <BaseCheckbox.HiddenInput ref={ref} />
        </BaseCheckbox.Root>
        {supportingText ? (
          <div
            className="ox-reset ox-CheckboxSupportingTextContainer"
            data-size={checkboxSize}
          >
            <p className={clsx("ox-reset ox-CheckboxSupportingText")}>
              {supportingText}
            </p>
          </div>
        ) : null}
      </Field.Root>
    );
  },
);
interface CheckboxGroupProps extends ArkCheckboxGroupProps {
  className?: string;
  labelClassNames?: FormLabelClassNames;
  children: React.ReactNode;
  helperText?: HelperTextProps;
  orientation?: "vertical" | "horizontal";
  label?: LabelOptionProps;
  asterisk?: boolean;
  error?: boolean;
  fieldProps?: FieldRootProps;
  classNames?: {
    root?: string;
    helperText?: string;
    list?: string;
    label?: string;
  };
  size?: "sm" | "md" | "lg";
  fontWeight?: "regular" | "medium" | "bold" | "semibold";
}

const CheckboxGroup = React.forwardRef<HTMLDivElement, CheckboxGroupProps>(
  (
    {
      className,
      classNames,
      children,
      orientation = "vertical",

      asterisk = false,
      helperText = { message: "", status: "default" },
      label,
      error,
      fieldProps,
      labelClassNames,
      size = "md",
      fontWeight = "regular",
      ...rest
    },
    ref,
  ) => {
    return (
      <CheckboxGroupContext.Provider value={{ size, fontWeight }}>
        <Field.Root {...fieldProps} invalid={error}>
          <BaseCheckboxGroup
            ref={ref}
            data-orientation={orientation}
            {...rest}
            className={clsx(
              "ox-reset ox-CheckboxGroupRoot",
              className,
              classNames?.root,
            )}
          >
            {label ? (
              <FormLabel
                label={label}
                size={size}
                error={error}
                classNames={labelClassNames}
              />
            ) : null}

            <div
              data-orientation={orientation}
              className={clsx(
                "ox-reset ox-CheckboxGroupList",
                classNames?.list,
              )}
            >
              {children}
            </div>
            {helperText?.message ? (
              <Field.HelperText
                data-size={size}
                data-status={helperText?.status}
                className={clsx(
                  "ox-reset ox-FormHelperText",
                  classNames?.helperText,
                )}
              >
                {getFormHelperText(helperText, size)}
              </Field.HelperText>
            ) : null}
          </BaseCheckboxGroup>
        </Field.Root>
      </CheckboxGroupContext.Provider>
    );
  },
);

Checkbox.displayName = "Checkbox";
CheckboxGroup.displayName = "CheckboxGroup";

export { Checkbox, CheckboxGroup };
export type { CheckboxProps, CheckboxGroupProps };
